import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, PowerOff, Power, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MaintenanceToggle
 *
 * Admin-only widget. Reads /api/admin/maintenance and lets an admin
 * flip the platform between LIVE and HOLDING with a single click.
 * State is persisted in Mongo — the change is instant and reversible.
 */
export function MaintenanceToggle({ token }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/maintenance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setState(await res.json());
    } catch (_e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const toggle = async (turnOn) => {
    if (busy) return;
    const confirmMsg = turnOn
      ? 'Enable Maintenance Mode?\n\nPublic visitors will see the holding page. Admin access is preserved.'
      : 'Disable Maintenance Mode?\n\nThe platform will be publicly accessible again.';
    if (!window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      const endpoint = turnOn ? 'enable' : 'disable';
      const res = await fetch(`${API_URL}/api/admin/maintenance/${endpoint}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('Toggle failed');
      const data = await res.json();
      setState(data.state);
      toast.success(turnOn ? 'Maintenance mode ENABLED' : 'Maintenance mode DISABLED');
    } catch (_e) {
      toast.error('Could not update maintenance mode');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          <span className="text-sm text-gray-500">Loading maintenance state…</span>
        </CardContent>
      </Card>
    );
  }

  const isOn = !!state?.enabled;

  return (
    <Card
      className={isOn ? 'border-2 border-amber-400 bg-amber-50' : 'border border-gray-200'}
      data-testid="maintenance-toggle-card"
    >
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div
            className={
              'w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ' +
              (isOn ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-500')
            }
          >
            {isOn ? <AlertTriangle className="w-6 h-6" /> : <Power className="w-6 h-6" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="font-semibold text-gray-900">Platform Access</p>
              <span
                className={
                  'text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ' +
                  (isOn ? 'bg-amber-500 text-white' : 'bg-emerald-500 text-white')
                }
                data-testid="maintenance-badge"
              >
                {isOn ? 'HOLDING' : 'LIVE'}
              </span>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              {isOn
                ? 'Public visitors are seeing the holding page. Admins retain full access.'
                : 'Platform is publicly accessible. Toggle to hide from the public.'}
            </p>
            {state?.updated_at && (
              <p className="text-xs text-gray-400 mb-3" data-testid="maintenance-updated-at">
                Last changed: {new Date(state.updated_at).toLocaleString()}
                {state.updated_by ? ' — by ' + state.updated_by : ''}
              </p>
            )}
            <Button
              onClick={() => toggle(!isOn)}
              disabled={busy}
              className={
                isOn
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  : 'bg-amber-500 hover:bg-amber-600 text-white'
              }
              data-testid="maintenance-toggle-button"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : isOn ? (
                <Power className="w-4 h-4 mr-2" />
              ) : (
                <PowerOff className="w-4 h-4 mr-2" />
              )}
              {isOn ? 'Restore Public Access' : 'Enable Maintenance Mode'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default MaintenanceToggle;
